import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys, LIVE_REFETCH_MS } from "@/api/queryClient";
import { MANAGEMENT_URL } from "@/config/env";
import type {
  CaseDetail,
  CaseEventView,
  CaseFileEntry,
  CaseListResult,
  CaseStatusValue,
  CaseStepsResult,
  CaseStepTranscriptResult,
} from "@/types";

// #301 (UC-5b): die Executions-Sicht. Den Scope entscheidet der Master (Admin sieht alle
// Läufe, sonst nur die eigenen) — die UI filtert nichts nach, sonst gäbe es zwei Wahrheiten.

export interface CaseFilter {
  ownerId?: string;
  instanceId?: string;
  templateId?: string;
  agentId?: string;
  status?: CaseStatusValue;
  limit?: number;
  offset?: number;
}

function queryString(filter: CaseFilter): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

/**
 * Läuft live nach: ein laufender Vorgang wandert von Schritt zu Schritt, und wer zusieht,
 * erwartet Bewegung ohne F5. `LIVE_REFETCH_MS` ist derselbe Takt wie bei Agents/Nodes.
 */
export function useCases(filter: CaseFilter = {}, opts: { enabled?: boolean } = {}): UseQueryResult<CaseListResult> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.list("cases"), queryString(filter)],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<CaseListResult>(`/cases${queryString(filter)}`);
    },
    // ⚠ `enabled` ist hier kein Luxus: ein leerer Filterwert fällt im Query-String weg —
    // aus einem noch unbekannten `instanceId` würde also „alle Läufe" statt „keine".
    enabled: !!session && (opts.enabled ?? true),
    refetchInterval: LIVE_REFETCH_MS,
  });
}

/** Das Detail trägt seit UC-5d die offene Rückfrage mit (`pendingAsk`). */
export function useCase(id: string | undefined): UseQueryResult<CaseDetail> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("cases", id) : ["resources", "cases", "_invalid"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<CaseDetail>(`/cases/${id}`);
    },
    enabled: !!session && !!id,
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useCaseEvents(id: string | undefined): UseQueryResult<CaseEventView[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.subList("cases", id, "events") : ["resources", "cases", "_invalid", "events", "list"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<CaseEventView[]>(`/cases/${id}/events`);
    },
    enabled: !!session && !!id,
    refetchInterval: LIVE_REFETCH_MS,
  });
}

// --- #303 (UC-2): die Schritt-Sicht -------------------------------------------------------
//
// „ich will in jedem Step sehen, was Input/Output war" (Arben). Die Faltung ist eine reine
// Projektion der Chronik — die UI rechnet nichts nach, sie zeigt, was der Master faltet.

export function useCaseSteps(id: string | undefined): UseQueryResult<CaseStepsResult> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.subList("cases", id, "steps") : ["resources", "cases", "_invalid", "steps", "list"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<CaseStepsResult>(`/cases/${id}/steps`);
    },
    enabled: !!session && !!id,
    refetchInterval: LIVE_REFETCH_MS,
  });
}

export function useCaseFiles(id: string | undefined): UseQueryResult<CaseFileEntry[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.subList("cases", id, "files") : ["resources", "cases", "_invalid", "files", "list"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<CaseFileEntry[]>(`/cases/${id}/files`);
    },
    enabled: !!session && !!id,
    refetchInterval: LIVE_REFETCH_MS,
  });
}

/**
 * Das Arbeitsprotokoll EINES Schritts — bewusst `enabled`-gesteuert (on-demand beim
 * Aufklappen). Es ist die grösste Nutzlast dieser Ebene, und jede Einsicht wird
 * serverseitig protokolliert; sie ungefragt für jeden Schritt zu ziehen wäre beides falsch.
 *
 * KEIN Live-Poll: das Protokoll entsteht am Schritt-ENDE und ändert sich danach nicht mehr.
 */
export function useCaseStepTranscript(
  caseId: string | undefined,
  stepId: string | null,
  enabled: boolean,
): UseQueryResult<CaseStepTranscriptResult> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey:
      caseId && stepId
        ? queryKeys.subDetail("cases", caseId, "transcript", stepId)
        : ["resources", "cases", "_invalid", "transcript", "_none"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<CaseStepTranscriptResult>(
        `/cases/${caseId}/steps/${encodeURIComponent(stepId ?? "")}/transcript`,
      );
    },
    enabled: !!session && !!caseId && !!stepId && enabled,
    // Ein 403 (Beteiligten-Sicht) ist eine Aussage, kein Netzproblem — nicht wiederholen.
    retry: false,
  });
}

/**
 * Datei-Download über die LAUF-Route (`GET /cases/:id/files/:attachmentId`) — nicht über den
 * Attachment-Store: dort gilt die Owner-Logik, hier die Lauf-Beteiligung. Bytes werden mit
 * Bearer-Header geholt und als Blob gespeichert (kein `<a href>`, der Endpoint braucht Auth).
 * Fehler wirft — der Aufrufer toastet.
 */
export async function downloadCaseFile(
  token: string,
  caseId: string,
  attachmentId: string,
  filename: string,
): Promise<void> {
  const res = await fetch(
    `${MANAGEMENT_URL}/cases/${encodeURIComponent(caseId)}/files/${encodeURIComponent(attachmentId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Download fehlgeschlagen (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // #303 (Review L7): erst im nächsten Takt freigeben. Safari und ältere Firefox starten
  // den Download asynchron NACH dem Klick — eine sofortige Freigabe zog ihnen die Daten
  // unter den Füssen weg (leere Datei, keine Fehlermeldung).
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
